/**
 * AIAssistancePanel Component
 *
 * Collapsible panel for AI-powered text generation and improvement.
 */
import { useState } from 'react';
import {
  Sparkles,
  ChevronDown,
  ChevronUp,
  FileText,
  Wand2,
  Expand,
  Loader2,
  AlertCircle,
  Check,
  X,
} from 'lucide-react';
import { useAI } from '../../hooks/useAI';
import { useEditorStore } from '../../stores/editorStore';
import { SECTION_TYPES, ImprovementType, ExpansionLength } from '../../types/editor';

interface AIAssistancePanelProps {
  offertaId?: string;
  context?: Record<string, unknown>;
  onInsertContent: (content: string) => void;
  onReplaceSelection: (content: string) => void;
  className?: string;
}

export default function AIAssistancePanel({
  offertaId,
  context = {},
  onInsertContent,
  onReplaceSelection,
  className = '',
}: AIAssistancePanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<'generate' | 'improve' | 'expand'>('generate');
  const [customInstructions, setCustomInstructions] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');

  // Use selectors to prevent unnecessary re-renders
  const selectedText = useEditorStore((state) => state.selectedText);
  const content = useEditorStore((state) => state.content);

  const {
    isLoading,
    isStreaming,
    streamContent,
    isAIEnabled,
    isAIConfigured,
    usageStats,
    generate,
    improve,
    expand,
    generateStream,
    cancelStream,
  } = useAI({
    offertaId,
    onSuccess: (content) => {
      setPreviewContent(content);
      setShowPreview(true);
    },
  });

  // Handle section generation
  const handleGenerate = async (sectionType: string, useStream = false) => {
    if (useStream) {
      await generateStream({
        sectionType,
        context,
        customInstructions: customInstructions || undefined,
        onChunk: (chunk) => {
          setPreviewContent(prev => prev + chunk.content);
        },
      });
      setShowPreview(true);
    } else {
      await generate(sectionType, context, customInstructions || undefined);
    }
  };

  // Handle text improvement
  const handleImprove = async (type: ImprovementType) => {
    if (!selectedText) return;
    await improve(selectedText, type, content);
  };

  // Handle text expansion
  const handleExpand = async (length: ExpansionLength) => {
    if (!selectedText) return;
    await expand(selectedText, length, content);
  };

  // Accept generated content
  const handleAccept = () => {
    const contentToInsert = previewContent || streamContent;
    if (selectedText) {
      onReplaceSelection(contentToInsert);
    } else {
      onInsertContent(contentToInsert);
    }
    setShowPreview(false);
    setPreviewContent('');
  };

  // Reject generated content
  const handleReject = () => {
    setShowPreview(false);
    setPreviewContent('');
    if (isStreaming) {
      cancelStream();
    }
  };

  if (!isAIEnabled) {
    return (
      <div className={`bg-gray-50 border-t border-gray-200 p-4 ${className}`}>
        <div className="flex items-center gap-2 text-gray-500">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">
            L'assistente AI non è abilitato per questo tenant.
            {!isAIConfigured && ' Configura le impostazioni AI nelle impostazioni del tenant.'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border-t border-gray-200 ${className}`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          <span className="font-medium text-gray-900">Assistente AI</span>
          {usageStats && usageStats.limits && (
            <span className="text-xs text-gray-500">
              ({usageStats.requests_this_hour}/{usageStats.limits.max_requests_per_hour} richieste/ora)
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 border-t border-gray-100">
          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            <TabButton
              active={activeTab === 'generate'}
              onClick={() => setActiveTab('generate')}
              icon={<FileText className="h-4 w-4" />}
              label="Genera"
            />
            <TabButton
              active={activeTab === 'improve'}
              onClick={() => setActiveTab('improve')}
              icon={<Wand2 className="h-4 w-4" />}
              label="Migliora"
              disabled={!selectedText}
            />
            <TabButton
              active={activeTab === 'expand'}
              onClick={() => setActiveTab('expand')}
              icon={<Expand className="h-4 w-4" />}
              label="Espandi"
              disabled={!selectedText}
            />
          </div>

          {/* Tab Content */}
          {activeTab === 'generate' && (
            <GenerateTab
              sections={SECTION_TYPES}
              onGenerate={handleGenerate}
              isLoading={isLoading}
              customInstructions={customInstructions}
              onCustomInstructionsChange={setCustomInstructions}
            />
          )}

          {activeTab === 'improve' && (
            <ImproveTab
              onImprove={handleImprove}
              isLoading={isLoading}
              hasSelection={!!selectedText}
            />
          )}

          {activeTab === 'expand' && (
            <ExpandTab
              onExpand={handleExpand}
              isLoading={isLoading}
              hasSelection={!!selectedText}
            />
          )}

          {/* Preview Panel */}
          {(showPreview || isStreaming) && (
            <div className="mt-4 border border-purple-200 rounded-lg overflow-hidden">
              <div className="bg-purple-50 px-3 py-2 flex items-center justify-between">
                <span className="text-sm font-medium text-purple-700">
                  {isStreaming ? 'Generazione in corso...' : 'Anteprima contenuto generato'}
                </span>
                <div className="flex items-center gap-2">
                  {isStreaming && (
                    <button
                      onClick={cancelStream}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Annulla
                    </button>
                  )}
                </div>
              </div>
              <div className="p-3 bg-white max-h-48 overflow-auto">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                  {previewContent || streamContent}
                  {isStreaming && <span className="animate-pulse">▊</span>}
                </pre>
              </div>
              {!isStreaming && (previewContent || streamContent) && (
                <div className="bg-gray-50 px-3 py-2 flex justify-end gap-2">
                  <button
                    onClick={handleReject}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-100"
                  >
                    <X className="h-4 w-4" />
                    Scarta
                  </button>
                  <button
                    onClick={handleAccept}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-purple-600 rounded-md hover:bg-purple-700"
                  >
                    <Check className="h-4 w-4" />
                    {selectedText ? 'Sostituisci' : 'Inserisci'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Tab Button Component
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
}

function TabButton({ active, onClick, icon, label, disabled = false }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
        ${active
          ? 'bg-purple-100 text-purple-700'
          : disabled
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100'
        }
      `}
    >
      {icon}
      {label}
    </button>
  );
}

// Generate Tab Component
interface GenerateTabProps {
  sections: typeof SECTION_TYPES;
  onGenerate: (sectionType: string, useStream?: boolean) => void;
  isLoading: boolean;
  customInstructions: string;
  onCustomInstructionsChange: (value: string) => void;
}

function GenerateTab({
  sections,
  onGenerate,
  isLoading,
  customInstructions,
  onCustomInstructionsChange,
}: GenerateTabProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Seleziona tipo di sezione
        </label>
        <div className="grid grid-cols-2 gap-2">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => onGenerate(section.id)}
              disabled={isLoading}
              className="flex flex-col items-start p-3 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="font-medium text-sm text-gray-900">{section.label}</span>
              <span className="text-xs text-gray-500 mt-0.5">{section.description}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Istruzioni personalizzate (opzionale)
        </label>
        <textarea
          value={customInstructions}
          onChange={(e) => onCustomInstructionsChange(e.target.value)}
          placeholder="Es: Usa un tono formale, menziona la nostra esperienza ventennale..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          rows={2}
        />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 text-purple-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Generazione in corso...</span>
        </div>
      )}
    </div>
  );
}

// Improve Tab Component
interface ImproveTabProps {
  onImprove: (type: ImprovementType) => void;
  isLoading: boolean;
  hasSelection: boolean;
}

function ImproveTab({ onImprove, isLoading, hasSelection }: ImproveTabProps) {
  const improvements: { type: ImprovementType; label: string; description: string }[] = [
    { type: 'general', label: 'Miglioramento generale', description: 'Migliora stile e chiarezza' },
    { type: 'clarity', label: 'Più chiaro', description: 'Semplifica e rendi più leggibile' },
    { type: 'persuasion', label: 'Più persuasivo', description: 'Aumenta l\'impatto commerciale' },
    { type: 'formal', label: 'Più formale', description: 'Linguaggio professionale' },
    { type: 'concise', label: 'Più conciso', description: 'Riduci senza perdere significato' },
  ];

  if (!hasSelection) {
    return (
      <div className="text-center py-6 text-gray-500">
        <Wand2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Seleziona del testo nell'editor per migliorarlo</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {improvements.map((item) => (
        <button
          key={item.type}
          onClick={() => onImprove(item.type)}
          disabled={isLoading}
          className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="text-left">
            <span className="font-medium text-sm text-gray-900">{item.label}</span>
            <span className="text-xs text-gray-500 ml-2">{item.description}</span>
          </div>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
          ) : (
            <Wand2 className="h-4 w-4 text-gray-400" />
          )}
        </button>
      ))}
    </div>
  );
}

// Expand Tab Component
interface ExpandTabProps {
  onExpand: (length: ExpansionLength) => void;
  isLoading: boolean;
  hasSelection: boolean;
}

function ExpandTab({ onExpand, isLoading, hasSelection }: ExpandTabProps) {
  const expansions: { length: ExpansionLength; label: string; description: string }[] = [
    { length: 'short', label: 'Espansione breve', description: '+25% contenuto' },
    { length: 'medium', label: 'Espansione media', description: '+50% contenuto' },
    { length: 'long', label: 'Espansione lunga', description: '+100% contenuto' },
  ];

  if (!hasSelection) {
    return (
      <div className="text-center py-6 text-gray-500">
        <Expand className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Seleziona del testo nell'editor per espanderlo</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {expansions.map((item) => (
        <button
          key={item.length}
          onClick={() => onExpand(item.length)}
          disabled={isLoading}
          className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="text-left">
            <span className="font-medium text-sm text-gray-900">{item.label}</span>
            <span className="text-xs text-gray-500 ml-2">{item.description}</span>
          </div>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
          ) : (
            <Expand className="h-4 w-4 text-gray-400" />
          )}
        </button>
      ))}
    </div>
  );
}
